import { authApi } from '@/lib/api/auth.api'
import {
  captureIdentityScope,
  registerLocalLogoutTransition,
} from '@/lib/api/request-scope'
import { desktopAccountSession } from '@/lib/utils/native-credential-store'
import { desktopSecureStorage, isTauri } from '@/lib/utils/tauri'
import { captureProfileIdentityScope } from '@/store/auth.store'

import type { SessionSnapshot } from '@scrambl/shared/session-scope'
import { SessionChangedError } from '@scrambl/shared/session-scope'

/** Accept only the single signed-out transition produced by our own removal. */
function assertRemovedOwner(before: SessionSnapshot, after: SessionSnapshot) {
  if (
    after.identity !== null ||
    after.generation !== before.generation + (before.identity === null ? 0 : 1)
  ) {
    throw new SessionChangedError()
  }
}

/**
 * Confirm revocation before removing credentials. Callers must check the returned
 * scope immediately before clearing caches, stores or navigating after any await.
 */
export async function logoutSession() {
  const identity = captureIdentityScope()
  const profile = captureProfileIdentityScope()
  const credentials = isTauri() ? desktopAccountSession.capture() : null
  const response = await authApi.logout()
  identity.assertCurrent()
  profile.assertCurrent()
  credentials?.assertCurrent()

  if (credentials) {
    const release = registerLocalLogoutTransition(credentials)
    let removalError: unknown
    try {
      await desktopSecureStorage.deleteTokens(credentials)
    } catch (error) {
      removalError = error
    } finally {
      release()
    }
    const removed = desktopAccountSession.capture()
    // Another window can complete the same removal while this window's vault
    // write loses its revision race. The exact empty successor is idempotent;
    // every replacement/round trip still rejects cleanup.
    if (
      removalError &&
      (removed.identity !== null ||
        removed.generation !==
          credentials.generation + (credentials.identity === null ? 0 : 1))
    ) {
      identity.assertCurrent()
      profile.assertCurrent()
      credentials.assertCurrent()
      throw removalError
    }
    assertRemovedOwner(credentials, removed)
    // The native account-change listener may already have cleared the profile.
    // A -> B -> A (or B -> null) must not count as this logout's own transition.
    const nextProfile = captureProfileIdentityScope()
    if (nextProfile.identity === null) assertRemovedOwner(profile, nextProfile)
    else profile.assertCurrent()
  }

  const completion = captureIdentityScope()
  const completedProfile = captureProfileIdentityScope()
  const completedCredentials = credentials
    ? desktopAccountSession.capture()
    : null
  return {
    response,
    assertCurrent() {
      completion.assertCurrent()
      completedProfile.assertCurrent()
      completedCredentials?.assertCurrent()
    },
  }
}
